import React, { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import Navbar from "./Navbar/Navbar";
import WelcomeSection from "./WelcomeSection/WelcomeSection";
import MintSection from "./MintSection/MintSection";
import { useEthereum } from "../../utils/useEthereum";
import { isMobile } from "react-device-detect";
import { Alert, AlertTitle } from "@material-ui/lab";
import {
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import { config } from "../../config/config";
import { openInNewTab } from "../../utils/openLink";
import CloseIcon from "@material-ui/icons/Close";
import { CHAINS_NAMES } from "../../utils/contract";
import Footer from "./Footer/Footer";
import { useMerkle } from "../../utils/useMerkle";

export default function LandingPage() {
  const classes = useStyles();
  const {
    isConnectedToChain,
    requestAccount,
    account,
    mint,
    phaseOneMint,
    phaseTwoMint,
    giftMint,
    disconnect,
    fetchPublicAmountMinted,
  } = useEthereum();
  const { getLeafAndProof } = useMerkle();
  const [mobileAppAlertOpen, setMobileAppAlertOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  function showError(message: string) {
    setErrorMessage(message);
    setIsError(true);
    setTimeout(() => {
      setIsError(false);
      setErrorMessage("");
    }, 3000);
  }

  async function handleRequestAccount() {
    try {
      await requestAccount();
    } catch (e) {
      console.log("error requestion account", e);
    }
  }

  async function handleMint(count: number) {
    try {
      setIsLoading(true);
      await mint(count);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      showError(e?.message);
    }
  }

  async function handlePhaseOneMint(count: number) {
    const { proof } = getLeafAndProof(account, "1");
    try {
      setIsLoading(true);
      await phaseOneMint(count, proof);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      showError(e?.message);
    }
  }

  async function handlePhaseTwoMint(count: number) {
    const { proof } = getLeafAndProof(account, "2");
    try {
      setIsLoading(true);
      await phaseTwoMint(count, proof);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      showError(e?.message);
    }
  }

  async function handleGiftClaim(count: number) {
    const { proof } = getLeafAndProof(account, "3");
    try {
      setIsLoading(true);
      await giftMint(count, proof);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      showError(e?.message);
    }
  }

  useEffect(() => {
    if (isMobile) {
      setMobileAppAlertOpen(!account);
    }
  }, [account]);

  return (
    <div className={classes.root}>
      <Backdrop style={{ zIndex: 1000, color: "#fff" }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={isError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
      <Navbar />
      <WelcomeSection
        fetchPublicAmountMinted={fetchPublicAmountMinted}
        isConnected={!!account}
        requestAccount={handleRequestAccount}
        disconnect={disconnect}
      />
      <MintSection
        mint={handleMint}
        phaseOneMint={handlePhaseOneMint}
        phaseTwoMint={handlePhaseTwoMint}
        giftMint={handleGiftClaim}
        isOnCorrectChain={isConnectedToChain}
      />
      <Footer />
      <div className={classes.chainAlert}>
        {mobileAppAlertOpen ? (
          <Alert
            severity="warning"
            onClose={() => setMobileAppAlertOpen(false)}
            action={
              <div>
                <Button
                  className={classes.openMetamaskButton}
                  onClick={() => openInNewTab(config.metamaskDeeplink)}
                  color="inherit"
                  size="small"
                >
                  Open Metamask
                </Button>
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setMobileAppAlertOpen(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </div>
            }
          >
            Using a mobile device?
          </Alert>
        ) : null}

        {isConnectedToChain ? null : (
          <Alert severity="warning">
            <AlertTitle>Warning</AlertTitle>
            Please make sure you are connected to the{" "}
            {CHAINS_NAMES[config.contractChainId]}
          </Alert>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: "relative",
    },
    chainAlert: {
      position: "fixed",
      display: "flex",
      justifyContent: "center",
      bottom: theme.spacing(2),
      right: 0,
      left: 0,
    },
    openMetamaskButton: {
      marginRight: theme.spacing(2),
    },
  })
);
